import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { getFeatureFlagsApi } from './api/featureFlagsApi';
import { useDebounce } from '../utils/debounce';
// Create context with undefined default
const FeatureFlagContext = createContext(undefined);
/**
 * Provider component that replaces FlagsmithProvider
 * Automatically fetches and manages feature flags state based on selected pharmacy
 *
 * Features:
 * - Automatically fetches flags when pharmacy changes
 * - Stateless regarding user types (backend handles authorization)
 * - No client-side caching - always fetches fresh data
 * - Provides manual fetch methods for component-driven updates
 */
export const FeatureFlagProvider = ({ children, apiUrl, tokenKey = 'token', onAuthError, pharmacyId, }) => {
    const [state, setState] = useState({
        flags: {},
        isLoading: true, // Start with loading true, will be set to false after initial fetch
        error: null,
        lastFetched: null,
    });
    const api = useMemo(() => getFeatureFlagsApi(apiUrl, tokenKey, onAuthError), [apiUrl, tokenKey, onAuthError]);
    // Debounced fetch function to prevent rapid API calls
    const fetchFlags = useCallback(async (pharmacyId) => {
        if (!pharmacyId) {
            // No pharmacy selected, set loading to false with empty flags
            setState(prev => ({
                ...prev,
                isLoading: false,
                flags: {},
                error: null,
                lastFetched: new Date(),
            }));
            return;
        }
        setState(prev => ({ ...prev, isLoading: true, error: null }));
        try {
            const response = await api.getFeatureFlags(pharmacyId);
            // Transform response to match Flagsmith format
            const flags = {};
            Object.entries(response).forEach(([key, enabled]) => {
                flags[key] = { enabled };
            });
            setState({
                flags,
                isLoading: false,
                error: null,
                lastFetched: new Date(),
            });
        }
        catch (error) {
            console.error('Failed to fetch feature flags:', error);
            setState(prev => ({
                ...prev,
                isLoading: false,
                error: error instanceof Error ? error.message : 'Failed to fetch feature flags',
            }));
        }
    }, [api]);
    // Debounce API calls by 300ms to prevent rapid successive requests
    const debouncedFetchFlags = useDebounce(fetchFlags, 300);
    // Automatically fetch flags when provider mounts or pharmacy changes
    useEffect(() => {
        debouncedFetchFlags(pharmacyId || null);
    }, [debouncedFetchFlags, pharmacyId]);
    // Manual method: Get pharmacy-specific flags (for component-driven fetching)
    const getPharmacyFlags = useCallback(async (pharmacyId) => {
        try {
            const response = await api.getFeatureFlags(pharmacyId);
            // Transform response to match Flagsmith format
            const flags = {};
            Object.entries(response).forEach(([key, enabled]) => {
                flags[key] = { enabled };
            });
            return flags;
        }
        catch (error) {
            console.error('Failed to fetch pharmacy flags:', error);
            throw error;
        }
    }, [api]);
    // Manual method: Get admin view (for admin components)
    const getAdminView = useCallback(async () => {
        try {
            return await api.getAdminView();
        }
        catch (error) {
            console.error('Failed to fetch admin view:', error);
            throw error;
        }
    }, [api]);
    // Manual method: Update flags state (for components to update context after fetching)
    const updateFlags = useCallback((newFlags) => {
        setState({
            flags: newFlags,
            isLoading: false,
            error: null,
            lastFetched: new Date(),
        });
    }, []);
    const contextValue = {
        ...state,
        // Methods for component-driven data fetching
        getPharmacyFlags,
        getAdminView,
        updateFlags,
    };
    return (_jsx(FeatureFlagContext.Provider, { value: contextValue, children: children }));
};
/**
 * Hook to access the feature flag context
 * Throws error if used outside of FeatureFlagProvider
 */
export const useFeatureFlagContext = () => {
    const context = useContext(FeatureFlagContext);
    if (context === undefined) {
        throw new Error('useFeatureFlagContext must be used within a FeatureFlagProvider');
    }
    return context;
};
export default FeatureFlagProvider;
